import React from 'react';
import PropTypes from 'prop-types';
import {
  useHelmet, TitleTag, MetaTag, LinkTag, ScriptTag
} from '@thd-nucleus/thd-helmet';
import {
  params,
  arrayOf,
  shape,
  string,
  json,
  number,
  useDataModel
} from '@thd-nucleus/data-sources';

import { getStoreDetailStructureData, getStructuredFAQ } from './structured-data';
import { getStructuredBreadcrumbs } from '../helpers/clean-breadcrumb-data';
import {
  getGraphQlReviewsOptions,
  getStoreDetails,
  getCurrentPromoImageSrc,
  getPreloadImageTag
} from '../helpers/pps-helpers';
import {
  EMT_PAGE_NAMES,
  PAGE_TYPE,
  LAST_PATH,
  SD_OG_IMAGE,
  OPEN_GRAPH_FACEBOOK_ADMIN,
} from '../constants';

const getStoreDetailsTitle = ({
  city,
  storeName = '',
  postalCode,
  state
}) => {
  const hasRequiredPostfixData = !!city && !!state && !!postalCode;
  const titlePrefix = 'The Home Depot';
  const postfix = hasRequiredPostfixData ? `Store in ${city}, ${state} ${postalCode}` : '';

  return `${titlePrefix} ${storeName} ${postfix}`;
};

const getMetaDescription = ({ city, storeName, state }) => {
  const hasRequiredDescriptionData = !!city && !!state && !!storeName;
  const descriptionPostfix = `${storeName} store in ${city}, ${state}.`;

  if (!hasRequiredDescriptionData) return 'The Home Depot';

  return 'Save time on your trip to the Home Depot by scheduling your order with buy online '
    + `pick up in store or schedule a delivery directly from your ${descriptionPostfix}`;
};

const getMetadataValues = (data) => {
  const { emtData, storeData } = data;
  const emtContentMetadata = emtData?.emtContent?.content?.metadata || {};
  const {
    city, storeName, postalCode, state, canonicalURL
  } = getStoreDetails({ data: storeData, path: LAST_PATH.DETAILS });

  const title = emtContentMetadata?.title || getStoreDetailsTitle({ city, storeName, postalCode, state });
  const metaDescription = emtContentMetadata?.metaDescription || getMetaDescription({ city, storeName, state });
  const ogTitle = emtContentMetadata?.ogTitle || getStoreDetailsTitle({ city, storeName, postalCode, state });
  const ogDescription = emtContentMetadata?.ogDescription || getMetaDescription({ city, storeName, state });
  const ogType = emtContentMetadata?.ogType || 'PPS Main Store';
  const ogImage = emtContentMetadata?.ogImage || SD_OG_IMAGE;
  const schema = emtContentMetadata?.schema;

  return {
    canonicalURL,
    title,
    metaDescription,
    ogTitle,
    ogDescription,
    ogType,
    ogImage,
    schema,
  };
};

const storeDetailsHandler = ({ props }) => {
  const tags = [];
  const { storeId } = props;
  const alternateLink = `android-app://com.thehomedepot/homedepot/storedetails/${storeId}`;

  const {
    title,
    canonicalURL,
    metaDescription,
    ogTitle,
    ogDescription,
    ogType,
    ogImage,
    schema
  } = getMetadataValues(props);

  tags.push(new TitleTag(title));
  tags.push(new MetaTag({ name: 'description', content: metaDescription, id: 'description' }));
  tags.push(new LinkTag({ rel: 'canonical', href: canonicalURL, id: 'canonical' }));
  tags.push(new MetaTag({ property: 'og:url', content: canonicalURL, id: 'canonicalURL' }));
  tags.push(new LinkTag({ rel: 'alternate', href: alternateLink, id: 'alternate' }));
  tags.push(new MetaTag({ property: 'og:type', content: ogType, id: 'ogType' }));
  tags.push(new MetaTag({ property: 'og:title', content: ogTitle, id: 'ogTitle' }));
  tags.push(new MetaTag({ property: 'og:description', content: ogDescription, id: 'ogDescription' }));
  tags.push(new MetaTag({ property: 'og:image', content: ogImage, id: 'ogImage' }));
  tags.push(new MetaTag({ property: 'og:site_name', content: 'The Home Depot', id: 'og-site_name' }));
  tags.push(new MetaTag({ property: 'fb:admins', content: OPEN_GRAPH_FACEBOOK_ADMIN, id: 'fb-admins' }));

  // We only load the first 6 images since the carousel only display 6 at a time.
  // The rest are lazy loaded.
  const topSellerMedia = props?.emtData?.emtContent?.content?.topSeller?.products;
  if (topSellerMedia) {
    for (let i = 0; i < 6; i++) { // eslint-disable-line no-plusplus
      const { media } = topSellerMedia[i] || {};
      if (media?.url) {
        tags.push(getPreloadImageTag(media?.url));
      }
    }
  }

  // Check for an active promotion and preload the image for the banner
  const promotionImageUrl = getCurrentPromoImageSrc(
    props?.emtData?.emtContent?.content?.storeFinderPromotions,
    EMT_PAGE_NAMES.DETAILS
  );
  if (promotionImageUrl) {
    tags.push(getPreloadImageTag(promotionImageUrl));
  }

  if (schema) {
    tags.push(new ScriptTag({ content: schema, id: 'storeDetailStructuredData' }));
  } else {
    const localBusinessSchema = getStoreDetailStructureData('LocalBusiness', props);
    const breadcrumbSchema = getStructuredBreadcrumbs({ props });
    const faqPageSchema = getStructuredFAQ(props, 'storeDetails');

    tags.push(new ScriptTag({ content: breadcrumbSchema, id: 'storeDetailStructuredBreadcrumb' }));
    tags.push(new ScriptTag({ content: localBusinessSchema, id: 'storeDetailStructuredLocalBusinessData' }));
    tags.push(new ScriptTag({ content: faqPageSchema, id: 'storeDetailStructuredFAQData' }));
  }

  return tags;
};

const StoreDetailMetadata = ({ storeId }) => {
  const storeSearchOptions = {
    variables: {
      storeSearchInput: storeId,
      pagesize: '1'
    }
  };

  const faqOption = {
    variables: {
      storeId,
    }
  };

  const emtContentOptions = {
    variables: {
      contentId: storeId,
      pageName: EMT_PAGE_NAMES.DETAILS
    }
  };

  const reviewsOption = getGraphQlReviewsOptions({ pageType: PAGE_TYPE.DETAILS, storeId });

  const { data: storeData, loading: storeLoading, error: storeError } = useDataModel('storeSearch', storeSearchOptions);
  const { data: emtData, loading: emtLoading, error: emtError } = useDataModel('emtContent', emtContentOptions);
  const { data: reviewData, loading: reviewLoading, error: reviewError } = useDataModel('storeReviews', reviewsOption);

  const props = {
    storeId, storeData, emtData, reviewData
  };

  useHelmet('storeDetailMetadata', { props }, storeDetailsHandler, [storeId]);

  return null;
};

StoreDetailMetadata.displayName = 'StoreDetailMetadata';

StoreDetailMetadata.dataModel = {
  storeSearch: params({
    storeSearchInput: string().isRequired(),
    pagesize: string().isRequired()
  }).shape({
    stores: arrayOf(shape({
      address: shape({
        city: string(),
        state: string(),
        postalCode: string(),
        street: string()
      }),
      storeId: string(),
      name: string(),
      phone: string(),
      storeDetailsPageLink: string(),
      storeHours: shape({
        sunday: shape({
          open: string(),
          close: string()
        }),
        monday: shape({
          open: string(),
          close: string()
        })
      })
    }))
  }),
  storeReviews: params({ storeId: string().isRequired() }).shape({
    Results: arrayOf(shape({
      Rating: number(),
      ReviewText: string(),
      Title: string(),
      UserNickname: string()
    })),
    Includes: shape({
      Products: shape({
        store: shape({
          FilteredReviewStatistics: shape({
            AverageOverallRating: string(),
            TotalReviewCount: number()
          })
        })
      })
    })
  }),
  emtContent: params({
    contentId: string().isRequired(),
    pageName: string().isRequired()
  }).shape({
    content: json()
  }),
};

StoreDetailMetadata.propTypes = {
  storeId: PropTypes.string.isRequired,
};

StoreDetailMetadata.defaultProps = {};

export { StoreDetailMetadata };